import type { TargetId } from "@common/target/types"

import {
  type TargetCategory,
  type TargetInfo,
  targetInfos,
  type TargetSlug,
} from "./info"

const getCurrentDomain = <KnownDomain extends string>(
  knownDomains: readonly KnownDomain[],
  fallbackDomain: NoInfer<KnownDomain>,
): KnownDomain => {
  const windowDomain = window.location.host

  if (knownDomains.includes(windowDomain as KnownDomain)) {
    return windowDomain as KnownDomain
  } else {
    return fallbackDomain
  }
}

const isTargetWhitelisted = (
  category: TargetCategory,
  targetWhitelistType: TargetWhitelistType,
): boolean => {
  switch (targetWhitelistType) {
    case "all":
      return true

    case "mainnet":
      return (() => {
        switch (category) {
          case "mainnet":
            return true
          case "beta":
          case "local":
          case "other":
            return false
        }
      })()

    case "beta":
      return (() => {
        switch (category) {
          case "beta":
            return true
          case "local":
          case "mainnet":
          case "other":
            return false
        }
      })()
  }
}

type TargetWhitelistType = "all" | "beta" | "mainnet"

const createDomainConfig = (
  targetWhitelistType: TargetWhitelistType,
  defaultTargetId: TargetId,
) => {
  let defaultTargetInfo: TargetInfo | undefined
  const idToTarget: Record<string, TargetInfo> = {}
  const slugToTarget: Record<string, TargetInfo> = {}

  for (const targetInfo of targetInfos) {
    if (isTargetWhitelisted(targetInfo.category, targetWhitelistType)) {
      idToTarget[targetInfo.id] = targetInfo
      slugToTarget[targetInfo.slug] = targetInfo

      if (targetInfo.id === defaultTargetId) {
        defaultTargetInfo = targetInfo
      }
    }
  }

  if (defaultTargetInfo === undefined) {
    throw new Error(
      `Internal config error, defaultTarget of ${defaultTargetId} incompatible with targetsShown of ${targetWhitelistType}`,
    )
  }

  return {
    defaultTargetInfo,
    availableTargets: Object.values(slugToTarget),
    getTargetBySlug: (slug: TargetSlug) => slugToTarget[slug],
    getTargetById: (id: TargetId) => idToTarget[id],
    mainnetIndexer: targetWhitelistType === "mainnet",
  }
}

export type DomainConfig = ReturnType<typeof createDomainConfig>

const rujiraDomainConfig = (): DomainConfig => {
  const knownDomains = [
    "rujira.network",
    "preview.rujira.network",
    "rujiperps.com",
    "testnet.rujiperps.com",
    "devnet.rujiperps.com",
  ] as const

  const fallbackDomain = ((): (typeof knownDomains)[number] => {
    switch (process.env.DEPLOY_STAGE) {
      case "production":
        return "rujiperps.com"
      case "staging":
        return "testnet.rujiperps.com"
      default:
        return "devnet.rujiperps.com"
    }
  })()

  const currentDomain = getCurrentDomain(knownDomains, fallbackDomain)

  switch (currentDomain) {
    case "devnet.rujiperps.com":
      return createDomainConfig("all", "rujidev")

    case "testnet.rujiperps.com":
      return createDomainConfig("beta", "rujibeta")

    case "rujira.network":
    case "preview.rujira.network": // Due to Rujira's Vercel config, preview is using mainnet
    case "rujiperps.com":
      return createDomainConfig("mainnet", "rujimainnet1")
  }
}

export const domainConfig = rujiraDomainConfig()

export const indexerUrl = domainConfig.mainnetIndexer
  ? "https://indexer-mainnet.levana.finance"
  : "https://indexer-testnet.levana.finance"

export const querierUrl = domainConfig.mainnetIndexer
  ? "https://querier-mainnet.rujiperps.com"
  : "https://querier-testnet.levana.finance"

export const govUrl = domainConfig.mainnetIndexer
  ? "https://gov.levana.finance"
  : "https://govtest.levana.finance"

export const shareUrl = "https://rujira.network/perps/share"
